import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://i.ibb.co/ZRpFcYDK/oie-s-XZ9-BUTHM5-Vz.png';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credential) => {
    setLoading(true);
    try {
      const result = await googleLogin(credential);
      
      // Check if new user needs to select plan
      if (result.is_new_user) {
        toast.info('Please select a plan to complete registration');
        navigate('/pricing?source=google');
        return;
      }
      
      // Existing user - login successful
      toast.success('Signed in with Google');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">
      {/* Top bar with logo linking home (visible on all breakpoints) */}
      <div className="absolute top-0 left-0 right-0 z-20 px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="login-logo-link">
          <img src={LOGO_URL} alt="DotCall" className="h-7 w-auto group-hover:opacity-90 transition-opacity" />
        </Link>
        <Link to="/pricing" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors" data-testid="login-top-pricing-link">
          View Pricing
        </Link>
      </div>

      <div className="flex flex-1">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #020617 0%, #0B1221 50%, #1a0533 100%)' }}>
        <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 30% 40%, rgba(245, 158, 11, 0.12) 0%, transparent 60%)' }} />
        <div className="relative z-10 p-16 max-w-lg">
          <Link to="/" className="inline-flex items-center gap-3 mb-12 group" data-testid="login-left-logo-link">
            <img src={LOGO_URL} alt="DotCall" className="h-9 w-auto group-hover:opacity-90 transition-opacity" />
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] text-white leading-tight mb-6">
            AI Voice Agents<br />
            <span className="text-amber-400">for Every Business</span>
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            Create intelligent voice agents that handle sales calls, support tickets, 
            and lead qualification automatically.
          </p>
          <div className="mt-12 flex gap-8">
            <div>
              <div className="text-3xl font-bold text-white font-['Outfit']">10M+</div>
              <div className="text-sm text-slate-500 mt-1">Calls Handled</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white font-['Outfit']">98%</div>
              <div className="text-sm text-slate-500 mt-1">Accuracy</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white font-['Outfit']">50+</div>
              <div className="text-sm text-slate-500 mt-1">Languages</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 pt-20" style={{ background: '#020617' }}>
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10">
            <Link to="/" className="inline-flex" data-testid="login-mobile-logo-link">
              <img src={LOGO_URL} alt="DotCall" className="h-8 w-auto" />
            </Link>
          </div>

          <h2 className="text-3xl font-bold font-['Outfit'] text-white mb-2">Welcome back</h2>
          <p className="text-slate-400 mb-8">Sign in to your account</p>

          {error && (
            <div className="mb-6 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-300 text-sm mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                data-testid="login-email-input"
                className="bg-slate-950 border-white/10 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 h-11 text-white placeholder:text-slate-600"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300 text-sm mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                data-testid="login-password-input"
                className="bg-slate-950 border-white/10 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 h-11 text-white placeholder:text-slate-600"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white font-medium shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)] transition-all"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign In <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#020617] px-3 text-slate-500">or</span></div>
          </div>

          <GoogleSignInButton onSuccess={handleGoogleSuccess} onError={(e) => setError(e)} text="signin_with" />

          <p className="text-center text-slate-500 mt-8 text-sm">
            Don't have an account?{' '}
            <Link to="/pricing" className="text-amber-400 hover:text-amber-300 transition-colors font-medium" data-testid="register-link">
              Choose a plan to sign up
            </Link>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}
